import React, {ChangeEvent, InputHTMLAttributes, useState} from 'react';
import styled, {css} from 'styled-components';
import {ReactComponent as EyeCloseIcon} from "assets/images/input/input_eye_close_black.svg";
import {ReactComponent as EyeOpenIcon} from "assets/images/input/input_eye_open_black.svg";
import {formatForId} from "../../helpers/formatString";
import {ExtraType} from "../../interfaces";
import {useCurrentLanguage} from "../../fsd/shared/lib";

interface IIcon {
    onClick?: () => void
    icon: React.ReactNode
    extra?: string
}

type TextFieldProps = {
    value: string,
    onChange: (e: ChangeEvent<HTMLInputElement>) => void,
    placeholder?: string,
    rightIcon?: IIcon,
    leftIcon?: IIcon,
    upContent?: string,
    leftContent?: string,
    rightContent?: string,
    type?: string,
    inputProps?: InputHTMLAttributes<HTMLInputElement>,
    readOnly?: boolean,
    regex?: string,
    extra?: ExtraType,
    inputExtra?: string,
    required?: boolean,
    isError?: boolean,
    errorText?: string,
    setErrorText?: (v: string) => void,
    successText?: string,
    setSuccessText?: (v: string) => void,
    id?: string,
    helperText?: string
    autoComplete?: 'off' | 'on' | 'new-password'

}

const TextField = (props: TextFieldProps) => {
    const {
        type = "text",
        extra = ``,
        leftIcon,
        upContent,
        leftContent,
        rightContent,
        rightIcon,
        inputProps,
        onChange,
        value,
        inputExtra = ``,
        placeholder,
        regex,
        readOnly = false,
        required = false,
        errorText,
        setErrorText,
        successText,
        setSuccessText,
        id,
        helperText,
        autoComplete = 'new-password',
        isError
    } = props;

    const [passwordVisible, setPasswordVisible] = useState(false);
    const _onChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (regex) {
            e.target.value = e.target.value.replace(new RegExp(regex, "g"), ``);
        }
        if (setErrorText) setErrorText('')
        if (setSuccessText) setSuccessText('')
        onChange(e);
    };

    const lang = useCurrentLanguage()


    // FIXME: inputExtra, input
    return (
        <Span extra={extra} readOnly={readOnly}>
            {leftIcon && <Icon extra={`right: unset; left: 5px;` + leftIcon.extra}> {leftIcon.icon} </Icon>}
            {upContent && <UpContent ar={lang === 'ar'}>{upContent}{required && ' *'}</UpContent>}
            <InputContainer inputExtra={inputExtra} errorText={isError || !!errorText}>
                {leftContent && <LeftContent ar={lang === 'ar'}>{leftContent}</LeftContent>}
                <StyledInput inputExtra={inputExtra} readOnly={readOnly}
                             value={value} placeholder={placeholder}
                             type={type === `password` && passwordVisible ? `text` : type}
                             onChange={_onChange} {...inputProps}
                             rightIcon={type === `password` || rightIcon !== undefined}
                             autoComplete={autoComplete} maxLength={inputProps?.maxLength || 180}
                             id={id}/>
                {rightContent && <RightContent ar={lang === 'ar'}>{rightContent}</RightContent>}
                {type === `password` ? (
                    <Eye ar={lang === 'ar'} onClick={() => setPasswordVisible(!passwordVisible)}>
                        {passwordVisible ? <EyeOpenIcon/> : <EyeCloseIcon/>}
                    </Eye>
                ) : rightIcon ? (
                    <Icon ar={lang === 'ar'} extra={rightIcon.extra} onClick={rightIcon.onClick}>
                        {rightIcon.icon}
                    </Icon>
                ) : null}

                {errorText &&
                    <ErrorText id={`input_error_text_${formatForId(placeholder || upContent || value)}`}>
                        {errorText}
                    </ErrorText>}
                {successText &&
                    <SuccessText id={`input_success_text_${formatForId(placeholder || upContent || value)}`}>
                        {successText}
                    </SuccessText>}
            </InputContainer>
            {(helperText && !errorText) &&
                <HelperText id={`input_helper_text_${formatForId(placeholder || upContent || value)}`}>
                    {helperText}
                </HelperText>}

        </Span>
    );
};

export default TextField;


const Span = styled.span<{ extra?: ExtraType, readOnly: boolean }>`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  max-width: 411px;
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 10px;
  position: relative;
  background: transparent;
  font-family: 'Roboto Condensed', serif;
  font-size: 20px;
  font-weight: 400;
  line-height: 28px;

  ${({readOnly}) =>
          readOnly &&
          css`
            cursor: default;
            border: none;

            :hover {
              border: none;
            }
          `}

  ${({extra}) => extra}
`;

const InputContainer = styled.div<{ inputExtra?: string, errorText?: boolean }>`
  box-sizing: border-box;
  max-height: 55px;
  height: inherit;
  width: 100%;
  position: relative;
  border-radius: 5px;
  border: 1px solid ${({errorText, theme}) => errorText
          ? theme.palette.error.primary
          : theme.palette.border.main};
  display: flex;
  align-items: center;

  ${({inputExtra}) => inputExtra}
  &:hover,
  &:active {
    box-shadow: 1px solid ${({theme}) => theme.palette.shadow.language};
  }
;
`

export const StyledInput = styled.input<{ inputExtra?: string, rightIcon?: boolean }>`
  &:focus {
    outline: none;
  }

  width: 100%;
  height: calc(100% - 2px);
  display: flex;
  flex: 1;
  margin: 0;
  color: ${({theme}) => theme.palette.color.main};
  background-color: ${({theme}) => theme.palette.background.secondary_2};
  box-sizing: border-box;
  padding: 10px 15px;

  border: none;
  border-radius: 5px;

  font-family: 'Roboto Condensed', 'serif';
  font-weight: 700;
  font-size: 18px;
  line-height: 21px;

  ::placeholder {
    font-family: 'Roboto Condensed', 'serif';
    font-weight: 700;
    font-size: 18px;
    line-height: 21px;
    color: ${({theme}) => theme.palette.color.placeholder};
  }

  // Убирает стили input:-internal-autofill-selected
  &:-webkit-autofill,
  &:-webkit-autofill:focus {
    width: 100%;
    height: inherit;
    border-radius: inherit;
    -webkit-text-fill-color: ${({theme}) => theme.palette.color.main} !important;
    -webkit-box-shadow: inset 0 0 0 50px ${({theme}) => theme.palette.background.secondary_2}; //т.к. background не переопределит стандартные стили браузера, используем этот хак
  }

  // Убирает стрелочки при type=number
  &:hover::-webkit-outer-spin-button,
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }


  ${({readOnly}) =>
          readOnly &&
          css`
            cursor: default;
            color: ${({theme}) => theme.palette.color.main};
          `}

  ${({rightIcon}) => rightIcon && `padding-inline-end: 40px;`}
  ${({inputExtra}) => inputExtra}
`;


const Icon = styled.div<{ extra?: string, ar?: boolean }>`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 22px;
  box-sizing: border-box;
  height: 22px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;


  & > svg > path[fill] {
    fill: ${({theme}) => theme.palette.svg.main};
  }

  & > svg > path[stroke] {
    stroke: ${({theme}) => theme.palette.svg.main};
  }

  ${({extra}) => extra}

  ${({ar}) =>
          ar &&
          css`
            left: 10px !important;
            right: auto;
          `}
`;

const UpContent = styled.div<{ ar: boolean }>`
  //margin-left: 5px;
  margin-bottom: 5px;
  display: flex;
  justify-content: flex-start;
  align-items: center;

  font-weight: 700;
  font-size: 18px;
  line-height: 21px;

  color: ${({theme}) => theme.palette.color.main};

  width: max-content;
`;


const LeftContent = styled.div<{ ar: boolean }>`
  margin: 0 15px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 18px;
  //color: ${({theme}) => theme.palette.color.placeholder};
`;

const RightContent = styled.div<{ ar: boolean }>`
  margin: 0 15px;
  display: flex;
  justify-content: center;
  align-items: center;

  color: ${({theme}) => theme.palette.color.main};
  font-family: 'Roboto Condensed', 'serif';
  font-weight: 700;
  font-size: 18px;
  line-height: 21px;
`;


const Eye = styled(Icon)<{ ar: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ar}) =>
          ar &&
          css`
            left: 10px !important;
            right: auto;
          `}
  & > svg > path {
    stroke: ${({theme}) => theme.palette.svg.main}
  }
`;

const ErrorText = styled.div`
  position: absolute;
  left: 0;
  top: 108%;
  font-family: 'Roboto Condensed', 'serif';
  font-weight: 700;
  font-size: 14px;
  line-height: 12px;

  color: ${({theme}) => theme.palette.error.primary};
`

const SuccessText = styled.div`
  position: absolute;
  left: 0;
  top: 108%;
  font-family: 'Roboto Condensed', 'serif';
  font-weight: 700;
  font-size: 14px;
  line-height: 12px;

  color: ${({theme}) => theme.palette.success.primary};
`

const HelperText = styled.div`
  position: absolute;
  bottom: -15px;
  font-family: 'Roboto Condensed', 'serif';
  font-weight: 700;
  font-size: 14px;
  line-height: 8px;

  color: ${({theme}) => theme.palette.color.placeholder};

  margin-top: 10px;

`